import { useEffect, useState } from 'react'
import { Filing } from '../../../../utilities/interfaces/Filing';
import Menu from '../../../../components/stateful/menu/Menu';
import useFilingFormDefinitions from './FilingFormDefinitions';
import TextArea from '../../../../components/stateful/textArea/TextArea';
import TextField from '../../../../components/stateful/textField/TextField';
import DropDown from '../../../../components/stateless/dropDown/DropDown';
import './FilingForm.scss';
import DatePicker from '../../../../components/stateless/datePicker/DatePicker';
import Checkbox from '../../../../components/stateless/checkbox/Checkbox';
import { useFilingFormController } from './filingFormController';
import CustomButton from '../../../../components/stateless/customButton/CustomButton';
import Signature from '../../../../components/stateful/signature/Signature';
import FilingUploads from './components/filingUpload/FilingUploads';
import MultiSelect from '../../../../components/stateful/multiSelect/MultiSelect';
import HearingNotice from './components/hearingNotice/HearingNotice';
import FilingHistoryCard from './components/history/FilingHistoryCard';
import StatusActions from '../../../../components/stateful/statusActions/statusActions';
import CustomDialog from '../../../../components/stateless/dialog/CustomDialog';
import HtmlRender from '../../../../components/stateless/htmlRender/HtmlRender';
import { WorkflowStatuses } from '../../../../utilities/enums/WorkflowStatuses';
import { Loader } from '@progress/kendo-react-indicators';
import { PDFExport } from "@progress/kendo-react-pdf";

interface IFilingFormProps {
  filingFormType: any;
  filingFormData: any;
  closeFilingForm: () => void;
  filing?: Filing;
  readOnly?: boolean;
}
const FilingForm = (props: IFilingFormProps) => {
  const { filing, filingFormType, filingFormData, closeFilingForm, readOnly } = props;
  const { getFilingTabsByType, getFilingFormDef, menuItems, filingFormDef } = useFilingFormDefinitions();
  const {
    // change handlers
    selectChange,
    handleMultiSelectChange,
    handleFormChange,
    datePickerChange,
    datePickerDateChange,
    datePickerTimeChange,
    checkBoxChange,
    // get values
    getDropDownValues,
    getDropdownLabelValue,
    getDropdownId,
    getMultiSelectValues,
    getFormValue,
    getCheckboxValue,
    getFilingActionsByDocumentType,
    getDropdownValueField,
    getStatus,
    // functions
    saveFiling,
    toggleHearingNoticeForm,
    saveHearingNotice,
    toggleConfirmationDialog,
    setStatusActionId,
    onDraftPreviewSelect,
    toggleDraftPreview,
    onAddNoteFieldChange,
    createNote,
    toggleAddNoteField,
    deleteFiling,
    toggleDeleteConfirmation,
    exportPDF,
    // data
    formValues,
    newHearingNotice,
    workflowSelected,
    histories,
    status,
    dialogOpen,
    hearingLock,
    workflowLock,
    hearingLockFields,
    workflowLockFields,
    roleStatusLocked,
    draftPreviewHtml,
    isDraftPreviewOpen,
    agencyRoleStatusLocked,
    filingNotes,
    newNote,
    addNoteFieldIsOpen,
    savingFiling,
    delConfModalIsOpen,
    pdfExport,
    draftName,
    filingCertificateOfAdoptionDocs
  } = useFilingFormController(closeFilingForm, filingFormData, filing);


  // state
  const [tab, setTab] = useState<string>('Status');
  const [filteredMenuItems, setFilteredMenuItems] = useState<any[]>([]);

  useEffect(() => {
    if (filingFormType) {
      getFilingTabsByType(filingFormType);
      if (!(formValues?.workflow?.id === 1)) {
        setFilteredMenuItems(menuItems
          .filter((x: { text: string; }) => x.text !== 'Hearing Notice' && x.text !== 'Hearing Summary'));
      }
      else {
        setFilteredMenuItems(menuItems);
      }
    }
    console.log('props - ', props);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues?.workflow?.id])

  const selectedTab = (item: any) => {
    getFilingFormDef(item.text);
    getFilingActionsByDocumentType(item.text);
    setTab(item.text);
  }

  const renderStatusTab = () => {
    return (
      <div>
        <h3>{status?.statusName}</h3>
        <div className="horizontal-divider" />
        {status?.id ? (
          <StatusActions
            statusId={status.id}
            filingId={filing?.id ?? -1}
            getStatus={getStatus}
            confirmAdvance={hearingLock}
            toggleConfirmationDialog={toggleConfirmationDialog}
            setStatusActionId={setStatusActionId}
          />
        ) : null}
      </div>
    )
  }

  const renderHistoryTab = () => {
    return (
      <div>
        { histories.map((history, key) => (
          <FilingHistoryCard
            history={history}
            key={key}
          />
        ))}
      </div>
    );
  }

  const renderFilesTab = () => {
    if (filing?.id) {
      return (
        <div>
          <FilingUploads filing={filing} readOnly={!!readOnly || roleStatusLocked} />
        </div>
      );
    } else {
      return (
        <h6>
          Please create/save the filing before uploading supplemental documents.
        </h6>
      )
    }
  }

  const renderFormField = (item: any, index: number) => {
    let isHearingNoticeField: boolean =
      item.name === 'hearingDate' ||
      item.name === 'nameOfPersonCommenting' ||
      item.name === 'affiliationOfPersonCommenting' ||
      item.name === 'hearingComments' ||
      item.name === 'agencyResponse';

    let isHearingReadOnly =
      isHearingNoticeField && status?.id &&
      status?.id !== WorkflowStatuses.New &&
      status?.id !== WorkflowStatuses.PublicHearingPending &&
      status?.id !== WorkflowStatuses.ReadyForPublicHearing &&
      status?.id !== WorkflowStatuses.AGReturnedToAgency &&
      status?.id !== WorkflowStatuses.GPROReturnedToAgency;

    switch (item.type) {
      case 'textarea':
        return (
          <div className='form-item'>
            <TextArea
              key={item.name + index}
              rows={item.rows ?? 5}
              title={item.label}
              value={getFormValue(item.name)}
              onChange={(event: any) => handleFormChange(item.name, event)}
              readOnly={readOnly || roleStatusLocked || isHearingReadOnly}
              required={!readOnly && (item.required ?? false)}
              fullwidth
            />
          </div>
        )
      case 'message':
        return (
          <span style={{ fontStyle: 'italic', color: '#808080' }}>{item.message ?? ''}</span>
        )
      case 'textfield':
        if (item.name === 'agencyEmail' && (!formValues?.workflow?.id || formValues?.workflow?.id !== 1)) {
          return;
        }
        return (
          <div className='form-item'>
            <TextField
              key={item.name + index}
              fullwidth
              title={item.label}
              value={getFormValue(item.name)}
              onChange={(event: any) => handleFormChange(item.name, event)}
              readOnly={readOnly || roleStatusLocked}
              required={!readOnly && !!item.required}
          />
          </div>
        )
      case 'checkbox':
        return (
          <div key={item.name + index}>
            <Checkbox
              label={item.label}
              onClick={() => { checkBoxChange(item.name) }}
              defaultValue={getCheckboxValue(item.name)}
              readOnly={readOnly || roleStatusLocked}
            />
          </div>
        )
      case 'dropdown':
        let listItems = getDropDownValues(item.name);
        let dropdownReadOnly = roleStatusLocked || (item.name === 'workflowSelect' && !!filing?.workflowId);
        return (
          <div className='form-item'>
            <DropDown
              key={item.name + index}
              label={item.label}
              value={formValues[getDropdownValueField(item.name) ?? '']}
              listItems={listItems}
              onSelect={(event: any) => selectChange(item.name, event.value)}
              size='fullwidth'
              dataKey={'id'}
              textField={getDropdownLabelValue(item.name)}
              readOnly={readOnly || dropdownReadOnly}
              idName={getDropdownId(item.name)}
              required={!readOnly && item.required}
            />
          </div>
        )
      case 'multiselect':
        let multiSelectItems = getMultiSelectValues(item.name);
        return (
          <div className='form-item'>
            <MultiSelect
              key={item.name + index}
              label={item.label}
              data={multiSelectItems}
              onChange={handleMultiSelectChange}
              dataKey={'id'}
              textfield={item.textfield}
              fullwidth
              required={!readOnly && item.required}
              values={formValues.drafts ?? []}
              disabled={readOnly || roleStatusLocked || (item.name === 'draftSelect' && !workflowSelected)}
            />
            {item.name === "draftSelect" && formValues.drafts && (
              <div>
                {formValues.drafts.map((draft: { id: number; draftName: string; }) => (
                  <CustomButton
                    isDropdown={true}
                    title={"Preview ".concat(draft.draftName)}
                    dropdownChildren={["Draft Copy", "Clean Copy"]}
                    onClick={() => {}}
                    onDropdownChildClick={(e) => {onDraftPreviewSelect(e.item, draft)}}
                  />
                ))}
              </div>
            )}
          </div>
        );
      case 'datepicker':
        return (
          <div className='form-item'>
            <DatePicker
              existingDate={!!formValues[item.name] && formValues[item.name] !== null && !(formValues[item.name] instanceof Date)
                ? new Date(formValues[item.name][formValues[item.name].length - 1] === 'Z' ? formValues[item.name] : formValues[item.name].concat('Z'))
                : new Date()}
              key={item.name + index}
              onChange={(event: any) => datePickerChange(item.name, event)}
              label={item.label}
              readOnly={
                readOnly ||
                roleStatusLocked ||
                isHearingReadOnly ||
                (item.name === "expiresOn" && agencyRoleStatusLocked)
              }
              required={!readOnly && (item.required ?? false)}
              separateTime={item.name === 'hearingDate'}
              onDateChange={(event: any) => datePickerDateChange(item.name, event)}
              onTimeChange={(event: any) => datePickerTimeChange(item.name, event)}
              min={item.name === 'hearingDate' ? new Date() : undefined}
              dateOnly={item.name === "fiscalDate" || item.name === "explanatoryDate" || item.name === "affidavitDate"}
              size='fullwidth'
            />
          </div>
        )
      case 'region':
        return (
          <div className="region" key={item.name + index}>
            <div style={{display:'flex', flexDirection:'row'}}>
              <div style={{display:'flex', marginRight:'10px'}}>{item.label}</div>
              {!readOnly && item.required ? (<div className="k-form-error">This field is required</div>) : null}
            </div>
            <div className="region-box">
              {
                item.children.map((childItem: any, childIndex: number) => {
                  return (renderFormField(childItem, childIndex));
                })
              }
            </div>
          </div>
        )
      case 'signature':
        return (
          <Signature />
        )
      default:
        return (<div key={'nothingFound' + index}></div>)
    }
  }

  const renderHearingTab = () => {
    return (
      <HearingNotice
        newHearingNotice={newHearingNotice}
        saveHearingNotice={saveHearingNotice}
        toggleHearingNoticeForm={toggleHearingNoticeForm}
        filingFormDef={filingFormDef}
        renderFormField={renderFormField}
        filingId={filing ? filing.id! : -1}
        disabled={readOnly || agencyRoleStatusLocked}
      />
    )
  }

  const renderConfirmationDialog = () => {
    return (
      <CustomDialog
        open={dialogOpen}
        title={"Form Submission Confirmation"}
        toggleDialog={() => {toggleConfirmationDialog(false)}}
        approveAction={() => {toggleConfirmationDialog(true)}}
        cancelAction={() => {toggleConfirmationDialog(false)}}
        size="medium"
      >
        <p>
          Are you sure you are done filling out this filing?
          Once you complete this action,
          you you will no longer be able to edit the following fields:
        </p>
        <ul>
          {workflowLock && workflowLockFields.map((field: string) => (
            <li>{field}</li>
          ))}
          {hearingLock && hearingLockFields.map((field: string) => (
            <li>{field}</li>
          ))}
        </ul>
      </CustomDialog>
    )
  }

  const renderNotesTab = () => {
    return (
      <div>
        {!!filing?.id ? (
          <div>
            {!readOnly && (
              <>
                {addNoteFieldIsOpen ? (
                  <div style={{display: 'flex', flexDirection: 'row'}}>
                    <TextField
                      onChange={onAddNoteFieldChange}
                    />
                    <CustomButton
                      onClick={createNote}
                      icon='add'
                      disabled={!!newNote && newNote.length < 1}
                    >
                      Create
                    </CustomButton>
                  </div>
                ) : (
                  <CustomButton
                    onClick={toggleAddNoteField}
                    icon='add'
                  >
                    Add Note
                  </CustomButton>
                )}
              </>
            )}
            {
              filingNotes.map(note => (
                <div className="k-card" style={{marginTop: '10px', marginLeft: '50px'}}>
                  <div className="k-card-body">
                    <p>{note.note}</p>
                    <h6 className="k-card-subtitle">{note.createdBy} - {new Date(note.createdOn).toDateString()}</h6>
                  </div>
                </div>
              ))
            }
          </div>
        ) : (
          <h6>
            Please create/save the filing before adding notes.
          </h6>
        )}
      </div>
    )
  }

  const renderDeleteConfirmation = () => {
    return (
      <CustomDialog
        open={delConfModalIsOpen}
        title={'Confirm Filing Deletion'} 
        toggleDialog={toggleDeleteConfirmation}
        approveAction={deleteFiling}
        cancelAction={toggleDeleteConfirmation}
        size='small'
        yesNo
      >
        <p>Are you sure you want to delete this filing?</p>
      </CustomDialog>
    )
  }

  return !isDraftPreviewOpen ? (
    <div>
      {renderConfirmationDialog()}
      {renderDeleteConfirmation()}
      <div className="filing-form">
        <div className='menu'>
          <Menu
            menuItems={filteredMenuItems}
            objectKey='text'
            onClick={selectedTab}
            vertical
            isCamelCaseItems={false}
            selectedTab={tab}
          />
        </div>
        <div className="form">

          {tab === 'Status' ||
          tab === 'History' ||
          tab === 'Files' ||
          tab === 'Hearing Notice' ||
          tab === 'Notes' ?
            <>
              <h3><b>{tab}</b></h3>
              {tab === 'Status' ? renderStatusTab() : null}
              {tab === 'History' ? renderHistoryTab() : null}
              {tab === 'Files' ? renderFilesTab() : null}
              {tab === 'Hearing Notice' ? renderHearingTab() : null}
              {tab === 'Notes' ? renderNotesTab() : null}
            </>
            :
            <div style={{display:'flex',flexDirection:'row'}}>
              <div style={{width:'500px'}}>
                <div className="form-header">
                  <h3><b>{tab}</b></h3>
                </div>
                {tab === 'Certificate of Adoption' && !readOnly && !(filingCertificateOfAdoptionDocs?.length > 0) && (
                  <div className="form-warning-banner">
                    <span className="k-icon k-i-warning" />
                    <span>Please fill out signer, or upload a Certificate of Adoption</span>
                  </div>
                )}
                {filingFormDef.map(
                  (item: any, index: number) => {
                    return (renderFormField(item, index))
                  }
                )}
              </div>
              <div style={{marginLeft:'10px'}}>
                {!readOnly && (
                  <div>
                    <CustomButton icon="save" onClick={saveFiling} disabled={savingFiling}>
                      {savingFiling && (<Loader type="converging-spinner" size='small' themeColor='light'/>)}
                      Save Filing
                    </CustomButton>
                    {(status?.id === WorkflowStatuses.New || status?.id === WorkflowStatuses.EmergencyNew || status?.id === WorkflowStatuses.NonSubstantiveNew) && (
                      <CustomButton icon="delete" themeColor='error' onClick={() => {toggleDeleteConfirmation()}}>
                        Delete Filing
                      </CustomButton>
                    )}
                  </div>
                )}
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  ) : (
    <div>
      <CustomButton onClick={toggleDraftPreview} icon="chevron-left" fillMode='flat' themeColor='primary' >Back</CustomButton>

                    <div className="dialog-content">
                        <div className="download-pdf-button">
                            <CustomButton
                                onClick={exportPDF}
                                icon="pdf"
                                themeColor="success"
                            >
                                Download PDF
                            </CustomButton>
                        </div>
                        <PDFExport
                            ref={pdfExport}
                            margin={40}
                            fileName={`${draftName}`}
                            author="NE-Rules"
                            paperSize="A4"
                            scale={0.6}
                        >
                          <HtmlRender value={draftPreviewHtml ?? ""}/>
                        </PDFExport>
                    </div>
    </div>
  )
}

export default FilingForm